import { getFileObjectsFromSecLoanAttachments } from '@sec-loans-ui/utils/getFileObjectsFromSecLoanAttachments'
import {
  SecLoanApplicationType,
  SecLoanAttachment,
  SecLoanPurposeType,
  SecLoansFileCategory,
  SecLoansHousingLoanTargetType,
  TargetAdditionalInfoData,
} from '@shared/sec-loans'
import * as fileUtils from '@ui-common/components/FormUpload/fileUtils'
import { convertUndefinedToNull } from '@ui-common/utils/form'
import { resolveFormEmptyString, resolveMapValue } from '@ui-common/utils/helpers'
import { IntlShape } from 'react-intl'

import { StepDataValidator, TargetAdditionalInfoStepData } from '../../../types'
import { isFormDataValid } from '../../../wizardData'
import { createFormValidator } from './formValidator'
import { TargetAdditionalInfoFormData } from './types'

export const getAndValidateAdditionalInfoTypeData: StepDataValidator<TargetAdditionalInfoData | undefined> = ({
  validationData,
}) => {
  let additionalInfo = validationData.targetInfo?.additionalInfoData
  const hasRenovationCosts =
    validationData.targetInfo?.renovationsInfoData?.renovationsAmount ||
    validationData.targetInfo?.renovationsInfoData?.otherPurchasesAmount

  if (!hasRenovationCosts && !!additionalInfo?.additionalRenovationsInfo) {
    additionalInfo = { ...validationData.targetInfo?.additionalInfoData, additionalRenovationsInfo: undefined }
  }

  const createValidator = (formatMessage: IntlShape['formatMessage']) =>
    createFormValidator(formatMessage, validationData)

  const isValid = isFormDataValid(additionalInfo, createValidator)
  return isValid ? additionalInfo : undefined
}

export const convertToFormValues = (
  targetAdditionalInfoStepData: TargetAdditionalInfoStepData | undefined,
  attachments?: SecLoanAttachment[],
): TargetAdditionalInfoFormData => {
  const data = {
    additionalInfo: targetAdditionalInfoStepData?.additionalInfo,
    additionalRenovationsInfo: targetAdditionalInfoStepData?.additionalRenovationsInfo,
    otherFiles: getFileObjectsFromSecLoanAttachments(
      targetAdditionalInfoStepData?.fileIds,
      attachments,
      SecLoansFileCategory.OtherLoanDocument,
    ),
    propertyManagerCertificateFiles: getFileObjectsFromSecLoanAttachments(
      targetAdditionalInfoStepData?.fileIds,
      attachments,
      SecLoansFileCategory.PropertyManagerCertificate,
    ),
    saleBrochureFiles: getFileObjectsFromSecLoanAttachments(
      targetAdditionalInfoStepData?.fileIds,
      attachments,
      SecLoansFileCategory.SaleBrochure,
    ),
    otherFilesFromBrokers: getFileObjectsFromSecLoanAttachments(
      targetAdditionalInfoStepData?.fileIds,
      attachments,
      SecLoansFileCategory.OtherDocumentFromBroker,
    ),
    budgetFiles: getFileObjectsFromSecLoanAttachments(
      targetAdditionalInfoStepData?.fileIds,
      attachments,
      SecLoansFileCategory.Budget,
    ),
    draftOfferFiles: getFileObjectsFromSecLoanAttachments(
      targetAdditionalInfoStepData?.fileIds,
      attachments,
      SecLoansFileCategory.DraftOffer,
    ),
  }
  return convertUndefinedToNull(data)
}

export const convertToSubmitData = (formData: TargetAdditionalInfoFormData): TargetAdditionalInfoStepData => {
  const {
    otherFiles,
    propertyManagerCertificateFiles,
    saleBrochureFiles,
    otherFilesFromBrokers,
    budgetFiles,
    draftOfferFiles,
  } = formData

  return {
    additionalInfo: resolveFormEmptyString(formData?.additionalInfo),
    additionalRenovationsInfo: resolveFormEmptyString(formData?.additionalRenovationsInfo),
    fileIds: fileUtils.extractFileIds([
      ...(otherFiles ?? []),
      ...(propertyManagerCertificateFiles ?? []),
      ...(saleBrochureFiles ?? []),
      ...(otherFilesFromBrokers ?? []),
      ...(budgetFiles ?? []),
      ...(draftOfferFiles ?? []),
    ]),
  }
}

export const getAdditionalInfoDescription = (intl: IntlShape, purpose: SecLoanPurposeType): string | undefined => {
  const isConstructionPurpose = purpose === SecLoanPurposeType.Construction
  const isRenovationPurpose = purpose === SecLoanPurposeType.Renovation

  if (isConstructionPurpose) {
    return intl.formatMessage({ id: 'sec-loans-additional-target-info-construction-purpose-description' })
  }
  if (isRenovationPurpose) {
    return intl.formatMessage({ id: 'sec-loans-additional-target-info-renovation-purpose-description' })
  }
  return intl.formatMessage({ id: 'sec-loans-additional-target-info-description' })
}

const defaultMapping: Record<SecLoansHousingLoanTargetType, SecLoansFileCategory[]> = {
  [SecLoansHousingLoanTargetType.HousingStock]: [
    SecLoansFileCategory.PropertyManagerCertificate,
    SecLoansFileCategory.SaleBrochure,
    SecLoansFileCategory.OtherDocumentFromBroker,
    SecLoansFileCategory.OtherLoanDocument,
  ],
  [SecLoansHousingLoanTargetType.RealEstate]: [
    SecLoansFileCategory.SaleBrochure,
    SecLoansFileCategory.OtherDocumentFromBroker,
    SecLoansFileCategory.Budget,
    SecLoansFileCategory.OtherLoanDocument,
  ],
  [SecLoansHousingLoanTargetType.RightOfResidence]: [
    SecLoansFileCategory.DraftOffer,
    SecLoansFileCategory.OtherLoanDocument,
  ],
  [SecLoansHousingLoanTargetType.PartOwnership]: [SecLoansFileCategory.OtherLoanDocument],
  [SecLoansHousingLoanTargetType.NotKnown]: [SecLoansFileCategory.OtherLoanDocument],
}

const constructionTargetMap: Record<SecLoansHousingLoanTargetType, SecLoansFileCategory[]> = {
  [SecLoansHousingLoanTargetType.HousingStock]: [],
  [SecLoansHousingLoanTargetType.RealEstate]: [],
  [SecLoansHousingLoanTargetType.RightOfResidence]: [],
  [SecLoansHousingLoanTargetType.PartOwnership]: [],
  [SecLoansHousingLoanTargetType.NotKnown]: [SecLoansFileCategory.Budget],
}

const housingLoanPurposeMap: Record<
  SecLoanPurposeType,
  Record<SecLoansHousingLoanTargetType, SecLoansFileCategory[]> | undefined
> = {
  [SecLoanPurposeType.Buy]: defaultMapping,
  [SecLoanPurposeType.Construction]: constructionTargetMap,
  [SecLoanPurposeType.Renovation]: undefined,
  [SecLoanPurposeType.LoanTransferToSbank]: undefined,
  [SecLoanPurposeType.Other]: undefined,
}

const returnDefaultMappingOnBuy: Record<
  SecLoanPurposeType,
  Record<SecLoansHousingLoanTargetType, SecLoansFileCategory[]> | undefined
> = {
  [SecLoanPurposeType.Buy]: defaultMapping,
  [SecLoanPurposeType.Construction]: undefined,
  [SecLoanPurposeType.Renovation]: undefined,
  [SecLoanPurposeType.LoanTransferToSbank]: undefined,
  [SecLoanPurposeType.Other]: undefined,
}

const applicationTypeToPurposeMap: Record<
  SecLoanApplicationType,
  Record<SecLoanPurposeType, Record<SecLoansHousingLoanTargetType, SecLoansFileCategory[]> | undefined> | undefined
> = {
  [SecLoanApplicationType.HousingLoan]: housingLoanPurposeMap,
  [SecLoanApplicationType.CottageLoan]: returnDefaultMappingOnBuy,
  [SecLoanApplicationType.PropertyInvestment]: returnDefaultMappingOnBuy,
  [SecLoanApplicationType.PurchaseLoan]: undefined,
}

export const getRequiredAttachmentCategory = (
  applicationType: SecLoanApplicationType,
  applicationPurpose: SecLoanPurposeType,
  targetType: SecLoansHousingLoanTargetType | undefined,
): SecLoansFileCategory[] => {
  const purposeToTargetMap = resolveMapValue(applicationTypeToPurposeMap, applicationType)
  if (!purposeToTargetMap) {
    return []
  }
  const targetToCategoryMap = resolveMapValue(purposeToTargetMap, applicationPurpose)
  if (!targetToCategoryMap) {
    return []
  }
  const result = resolveMapValue(targetToCategoryMap, targetType)

  if (!result) {
    return []
  }
  return result
}
